import { reportError } from '@/base/utils/errorReporting'

/*
 * Limit czasu fallbackowego fetcha. Ten fetch biegnie na ścieżce STARTOWEJ: `router.beforeEach`
 * czeka na `loadLanguage`, a `mountApp` czeka na `router.isReady()` - dopóki fetch wisi,
 * aplikacja SIĘ NIE MONTUJE i użytkownik widzi pustą stronę. Bez limitu zawieszone (nie zerwane)
 * połączenie - główny podejrzany na iOS - trzymałoby go tam do systemowego timeoutu (~60 s),
 * czyli GORZEJ niż stan sprzed fallbacku, gdzie padnięty import odrzucał się od razu i bramka
 * renderowała się z surowymi kluczami. 4 s to kompromis: mieści wolne łącze mobilne dla ~40 kB
 * JSON-a, a jednocześnie nie zamienia awarii tłumaczeń w awarię całej bramki.
 */
export const FALLBACK_FETCH_TIMEOUT_MS = 4000

export function fetchLanguageMessages(url, timeoutMs = FALLBACK_FETCH_TIMEOUT_MS) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  let timedOut = false
  const timer =
    controller && timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true
          controller.abort()
        }, timeoutMs)
      : null

  const options = { cache: 'reload' }
  if (controller) {
    options.signal = controller.signal
  }

  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        const error = new Error(`i18n fallback fetch: HTTP ${response.status}`)
        error.status = response.status
        throw error
      }
      return response.json().catch((parseError) => {
        const contentType = response.headers?.get?.('content-type') || 'brak content-type'
        const error = new Error(`i18n fallback fetch: nieprawidłowy JSON (${contentType})`)
        error.status = response.status
        error.cause = parseError
        throw error
      })
    })
    .catch((error) => {
      if (!timedOut) {
        throw error
      }
      // komunikat przerwanego fetcha jest zależny od silnika (`AbortError`, `Load failed`, …),
      // więc timeout dostaje własny, stały komunikat - inaczej rozsypałby się w Sentry na grupy
      const timeoutError = new Error(`i18n fallback fetch: przekroczony czas ${timeoutMs} ms`)
      timeoutError.timedOut = true
      timeoutError.cause = error
      throw timeoutError
    })
    .finally(() => {
      if (timer) {
        clearTimeout(timer)
      }
    })
}

function environmentDiagnostics() {
  return {
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
  }
}

const fallbackRequests = new Map()
const fallbackAttempts = new Map()
const reportedFailures = new Set()

/*
 * Porażka fallbacku jest raportowana RAZ na klucz (adres pliku, a przy jego braku - język)
 * na wizytę. Ponawianie samego pobrania zostaje: `router.beforeEach` woła `loadLanguage`
 * przy każdej nawigacji, więc kolejne przejścia nadal mają szansę wczytać tłumaczenia.
 * Bez tej bramki jedna sesja produkowała tyle zdarzeń `failed`, ile nawigacji, przy jednym
 * `recovered` - a pomiarem była PROPORCJA tych dwóch grup
 * (doc/robocze/webkit-import-chunkow.md §10.4). Bramka zostaje także po wyciszeniu `recovered`:
 * bez niej wolumen `failed` mierzyłby liczbę nawigacji zamiast liczby awarii. Numer próby jedzie
 * w `extra.attempt`.
 */
function reportFallbackFailureOnce(key, report) {
  if (reportedFailures.has(key)) {
    return
  }
  reportedFailures.add(key)
  report()
}

function recoverLanguageMessages(importError, url, lang) {
  if (fallbackRequests.has(url)) {
    return fallbackRequests.get(url)
  }
  const attempt = (fallbackAttempts.get(url) || 0) + 1
  fallbackAttempts.set(url, attempt)
  /*
   * ⚠️ SUKCES fallbacku NIE jest raportowany (od 18.08.2026) - była to druga najliczniejsza grupa
   * w projekcie (661 zdarzeń na dobę, 20% całości), a jej pomiar jest zakończony: skuteczność
   * fallbacku zmierzona dwa razy, na dwóch różnych bundlach - 2742 `recovered` vs 26 `failed`
   * + 3 `timeout` (17.08.2026) oraz 661 vs 6 (18.08.2026), czyli ~99% w obu próbach. Wniosek się
   * już nie zmieni: przyczyną padniętych importów jest transport do CDN, nie stan urządzenia
   * (gdyby chodziło o offline albo uśpioną kartę, drugie żądanie padałoby tak samo).
   *
   * Regresję samego fallbacku widać dalej po `failed`/`timeout` (6 zdarzeń na dobę) - gdyby
   * przestał działać, ta grupa urośnie o wolumen, który dziś jest niewidoczny. Żeby zmierzyć
   * PROPORCJĘ jeszcze raz (np. po zmianie konfiguracji CDN), dołóż tu `reportMessage` z tagiem
   * `i18nFallback: recovered` - bramka `reportFallbackFailureOnce` niżej istnieje właśnie po to,
   * żeby ta proporcja była policzalna.
   */
  const request = fetchLanguageMessages(url).catch((fetchError) => {
    fallbackRequests.delete(url)
    reportFallbackFailureOnce(url, () =>
      reportError(importError, {
        fallbackMessage: 'i18n: wczytanie tłumaczeń nieudane',
        tags: { flow: 'i18n', i18nFallback: fetchError?.timedOut ? 'timeout' : 'failed' },
        extra: {
          lang,
          url,
          attempt,
          fetchFallbackError: fetchError?.message,
          fetchFallbackStatus: fetchError?.status,
          ...environmentDiagnostics(),
        },
      })
    )
    throw new Error('loadLanguage error')
  })
  fallbackRequests.set(url, request)
  return request
}

export function loadLanguageMessages(loadModule, url, lang) {
  return loadModule().then(
    (module) => module.default,
    (importError) => {
      if (!url) {
        reportFallbackFailureOnce(`lang:${lang}`, () =>
          reportError(importError, {
            fallbackMessage: 'i18n: wczytanie tłumaczeń nieudane',
            tags: { flow: 'i18n', i18nFallback: 'unavailable' },
            extra: { lang, ...environmentDiagnostics() },
          })
        )
        throw new Error('loadLanguage error')
      }
      return recoverLanguageMessages(importError, url, lang)
    }
  )
}
